import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  imageUrl: string;
  date: string;
  viewCount: number;
  commentCount: number;
  isHot?: boolean;
  isNew?: boolean;
  variant?: 'default' | 'feature' | 'compact';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  category,
  categorySlug,
  imageUrl,
  date,
  viewCount,
  commentCount,
  isHot,
  isNew,
  variant = 'default',
}: ArticleCardProps) {
  const { t } = useTranslation();

  if (variant === 'feature') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block bg-foreground-900 rounded-xl overflow-hidden cursor-pointer relative h-full"
      >
        <div className="absolute inset-0">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>
        <div className="relative h-full flex flex-col justify-end p-5">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500/90 text-background-50 rounded-full w-fit mb-3">
            {category}
          </span>
          <h3 className="text-lg font-heading font-bold text-background-50 leading-snug group-hover:text-accent-300 transition-colors line-clamp-2">
            {title}
          </h3>
          <div className="mt-3 flex items-center gap-4 text-xs text-background-200/70">
            <span>{date}</span>
            <span>{t('views')} {viewCount.toLocaleString()}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block bg-background-50 rounded-lg overflow-hidden cursor-pointer border border-background-200/70 hover:border-accent-300 transition-colors"
      >
        <div className="flex gap-4 p-4">
          <div className="w-28 h-20 flex-shrink-0 rounded-md overflow-hidden">
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
              {title}
            </h4>
            <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
              <span>{date}</span>
              <span>{t('views')} {viewCount.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group block bg-background-50 rounded-xl overflow-hidden cursor-pointer border border-background-200/70 hover:border-accent-300 transition-all duration-300"
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {(isHot || isNew) && (
          <span
            className={`absolute top-3 left-3 px-2.5 py-1 text-xs font-semibold rounded-full ${
              isHot ? 'bg-accent-500 text-background-50' : 'bg-primary-500 text-background-50'
            }`}
          >
            {isHot ? 'HOT' : 'NEW'}
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-xs text-accent-600 font-medium mb-2">
          {category}
        </span>
        <h4 className="text-base font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
          {title}
        </h4>
        <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
          {excerpt}
        </p>
        <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
          <span>{date}</span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {viewCount.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" />
              {commentCount.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}